import {
  workspaceActivationRequestSchema,
  workspaceActivationResponseSchema,
  workspaceListResponseSchema,
  type AvailableWorkspace
} from '@inktap/contracts/workspace-organization';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { request } from '../lib/api';
import { CustomerNavigationIcon } from './CustomerNavigationIcon';
import { toSession, type UserSessionResponse } from '../lib/userSession';
import { useSession } from '../session';

const NAV_LINKS = [
  { to: '/dashboard', label: 'Home' },
  { to: '/tap', label: 'Tap Page' },
  { to: '/card-design', label: 'Card Design' },
  { to: '/orders', label: 'Orders' },
  { to: '/analytics', label: 'Analytics' },
  { to: '/contacts', label: 'Contacts' },
  { to: '/plan-billing', label: 'Plan & Billing' },
  { to: '/settings', label: 'Settings' }
] as const;
const ORGANIZATION_NAV_LINKS = [
  ...NAV_LINKS.slice(0, 6), { to: '/team', label: 'Team' }, ...NAV_LINKS.slice(6)
];


const getInitials = (email: string): string => {
  if (!email) {
    return 'U';
  }
  const [user] = email.split('@');
  if (!user) {
    return 'U';
  }
  const sanitized = user
    .split(/[._-]/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0))
    .join('');
  if (sanitized.length >= 2) {
    return sanitized.slice(0, 2).toUpperCase();
  }
  return sanitized.slice(0, 1).toUpperCase() || 'U';
};

const formatLinkClass = ({ isActive }: { isActive: boolean }): string =>
  `site-nav__link${isActive ? ' site-nav__link--active' : ''}`;

type SiteHeaderProps = {
  onSignOut?: () => void;
};

function SiteHeader({ onSignOut }: SiteHeaderProps) {
  const { session, replaceSession } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const navToggleRef = useRef<HTMLButtonElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const [workspaces, setWorkspaces] = useState<AvailableWorkspace[]>([]);
  const [nextWorkspaceCursor, setNextWorkspaceCursor] = useState<string | null>(null);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [switchingWorkspaceId, setSwitchingWorkspaceId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const accountToggleRef = useRef<HTMLButtonElement | null>(null);
  const workspaceLoadGeneration = useRef(0);

  const loadWorkspacePage = async (cursor?: string) => {
    const generation = ++workspaceLoadGeneration.current;
    if (!cursor) {
      setWorkspaces([]);
      setNextWorkspaceCursor(null);
    }
    setWorkspacesLoading(true);
    setWorkspaceError(null);
    try {
      const query = new URLSearchParams({ take: '20' });
      if (cursor) {
        query.set('cursor', cursor);
      }
      const response = workspaceListResponseSchema.parse(await request<unknown>(`/me/workspaces?${query.toString()}`));
      if (generation !== workspaceLoadGeneration.current) return;
      setWorkspaces((current) => {
        const combined = cursor ? [...current, ...response.items] : response.items;
        return Array.from(new Map(combined.map((workspace) => [workspace.id, workspace])).values());
      });
      setNextWorkspaceCursor(response.nextCursor);
    } catch {
      if (generation === workspaceLoadGeneration.current) setWorkspaceError('Unable to load workspaces.');
    } finally {
      if (generation === workspaceLoadGeneration.current) setWorkspacesLoading(false);
    }
  };

  useEffect(() => {
    if (!session) {
      workspaceLoadGeneration.current += 1;
      setWorkspaces([]);
      setNextWorkspaceCursor(null);
      return;
    }
    void loadWorkspacePage();
    return () => { workspaceLoadGeneration.current += 1; };
  }, [session?.expiresAt, session?.email]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) {
        return;
      }
      setMenuOpen(false);
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        accountToggleRef.current?.focus();
      }
    };

    window.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleKey);

    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!session) {
      setMenuOpen(false);
    }
  }, [session]);

  const focusCurrentNavigation = () => {
    const current = navRef.current?.querySelector<HTMLAnchorElement>('a[aria-current="page"]');
    const first = navRef.current?.querySelector<HTMLAnchorElement>('a');
    (current ?? first)?.focus();
  };

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const compact = window.matchMedia('(max-width: 768px)');
    const onChange = () => {
      if (!navRef.current?.closest('.app-shell--customer-refresh')) return;
      if (compact.matches && navRef.current?.contains(document.activeElement)) navToggleRef.current?.focus();
      if (!compact.matches && document.activeElement === navToggleRef.current) focusCurrentNavigation();
      setNavOpen(false);
    };
    compact.addEventListener('change', onChange);
    return () => compact.removeEventListener('change', onChange);
  }, []);

  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNavOpen(false);
        navToggleRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [navOpen]);

  const handleToggleMenu = () => {
    setNavOpen(false);
    setMenuOpen((previous) => !previous);
  };

  const handleSignOut = () => {
    setMenuOpen(false);
    onSignOut?.();
  };

  const handleWorkspaceSwitch = async (workspace: AvailableWorkspace) => {
    if (workspace.current || !workspace.canActivate || switchingWorkspaceId) {
      return;
    }
    setSwitchingWorkspaceId(workspace.id);
    setWorkspaceError(null);
    try {
      const payload = workspaceActivationRequestSchema.parse({ workspaceId: workspace.id });
      workspaceActivationResponseSchema.parse(await request<unknown>('/me/workspaces/activate', {
        method: 'POST',
        body: JSON.stringify(payload)
      }));
      const response = await request<UserSessionResponse>('/auth/session');
      replaceSession(toSession(response));
      window.location.assign('/dashboard');
    } catch {
      setWorkspaceError('Unable to switch workspaces.');
      setSwitchingWorkspaceId(null);
    }
  };

  const currentWorkspace = workspaces.find((workspace) => workspace.current) ?? null;
  const primaryLinks = currentWorkspace?.kind === 'ORGANIZATION' ? ORGANIZATION_NAV_LINKS : NAV_LINKS;
  // Current membership may be outside this bounded workspace-list page. The
  // account entry can safely resolve its organization on /team without scanning.
  const accountLinks = currentWorkspace?.kind === 'ORGANIZATION' || (!currentWorkspace && nextWorkspaceCursor)
    ? ORGANIZATION_NAV_LINKS : NAV_LINKS;

  return (
    <header className="site-nav">
      <div className="site-nav__inner">
        <Link to={session ? '/dashboard' : '/'} className="site-nav__brand" aria-label="InkTap home">
          <span className="brand-wordmark"><span className="brand-wordmark__ink">ink</span><span className="brand-wordmark__tap">tap</span></span>
        </Link>
        {session ? (
          <>
          <button type="button" className="site-nav__compact-toggle" ref={navToggleRef}
            aria-controls="customer-primary-nav" aria-expanded={navOpen}
            onBlur={(event) => {
              if (!event.relatedTarget && window.getComputedStyle(event.currentTarget).display === 'none') focusCurrentNavigation();
            }}
            onClick={() => { setMenuOpen(false); setNavOpen(open => !open); }}>
            {navOpen ? 'Close menu' : 'Menu'}
          </button>
          <nav className="site-nav__links" id="customer-primary-nav" aria-label="Primary" data-open={navOpen} ref={navRef}
            onBlur={(event) => {
              if (!event.relatedTarget && window.matchMedia('(max-width: 768px)').matches &&
                  window.getComputedStyle(event.currentTarget).display === 'none') navToggleRef.current?.focus();
            }}>
            {primaryLinks.map(({ to, label }) => (
              <NavLink key={to} to={to} className={formatLinkClass} onClick={() => { if (navOpen) document.getElementById('main-content')?.focus(); setNavOpen(false); }}>
                <CustomerNavigationIcon to={to} className="site-nav__icon" />
                {label}
              </NavLink>
            ))}
          </nav>
          </>
        ) : null}
        <div className="site-nav__actions">
          {session ? (
            <div className="site-nav__account" ref={menuRef}>
              <button
                type="button"
                className="site-nav__account-toggle"
                ref={accountToggleRef}
                onClick={handleToggleMenu}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-controls={menuOpen ? 'site-nav-account-menu' : undefined}
                aria-label="Account menu"
              >
                <span className="site-nav__avatar" aria-hidden="true">
                  {getInitials(session.email)}
                </span>
                <span className="site-nav__account-label">{currentWorkspace?.name ?? session.email}</span>
              </button>
              {menuOpen ? (
                <div className="site-nav__account-menu" id="site-nav-account-menu" role="menu">
                  <div className="site-nav__account-meta">
                    <span className="site-nav__account-email">{session.email}</span>
                    {currentWorkspace ? (
                      <span className="site-nav__account-workspace">
                        {currentWorkspace.kind === 'ORGANIZATION' ? 'Organization' : 'Personal'} · {currentWorkspace.name}
                      </span>
                    ) : null}
                    <span className="site-nav__account-plan">
                      Plan: {(session.subscriptionPlan ?? 'free').toUpperCase()}
                    </span>
                  </div>
                  {workspaces.length > 1 || nextWorkspaceCursor ? (
                    <div className="site-nav__workspace-section" aria-label="Choose workspace">
                      <span className="site-nav__menu-heading">Workspaces</span>
                      {workspaces.map((workspace) => (
                        <button
                          key={workspace.id}
                          type="button"
                          className={`site-nav__workspace-option${workspace.current ? ' site-nav__workspace-option--current' : ''}`}
                          disabled={workspace.current || !workspace.canActivate || Boolean(switchingWorkspaceId)}
                          onClick={() => void handleWorkspaceSwitch(workspace)}
                        >
                          <span className="site-nav__workspace-copy">
                            <span>{workspace.name}</span>
                            <small>
                              {workspace.kind === 'ORGANIZATION' ? 'Organization' : 'Personal'}
                              {!workspace.page ? ' · No page assigned' : ''}
                            </small>
                          </span>
                          <span className="site-nav__workspace-state">
                            {workspace.current
                              ? 'Current'
                              : switchingWorkspaceId === workspace.id
                                ? 'Switching…'
                                : workspace.canActivate
                                  ? 'Switch'
                                  : 'Unavailable'}
                          </span>
                        </button>
                      ))}
                      {nextWorkspaceCursor ? (
                        <button
                          type="button"
                          className="site-nav__workspace-more"
                          disabled={workspacesLoading}
                          onClick={() => void loadWorkspacePage(nextWorkspaceCursor)}
                        >
                          {workspacesLoading ? 'Loading…' : 'Load more workspaces'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {workspaceError ? <span className="site-nav__workspace-error" role="alert">{workspaceError}</span> : null}
                  {accountLinks.map(({ to, label }) => (
                    <NavLink key={to} to={to} className="site-nav__account-link" role="menuitem" onClick={() => setMenuOpen(false)}>
                      {label}
                    </NavLink>
                  ))}
                  <button
                    type="button"
                    className="site-nav__account-link site-nav__account-link--danger"
                    role="menuitem"
                    onClick={handleSignOut}
                  >
                    Log out
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="site-nav__auth-links">
              <Link to="/login" className="site-nav__secondary">
                Log in
              </Link>
              <Link to="/create-account" className="site-nav__cta">
                Create account
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export { SiteHeader };
